import {
    customPalette,
    MultiSeriesChartTypesOptions,
    SingleSeriesChartTypesOptions
} from '../chartConsts';
import {ChartConfig} from '../types';
import {
    genBasicChartData,
    genBubbleChartData,
    genScalesChartData,
    genMultiSeriesChartData,
    getFunnelChartData
} from '../serialize';
import {reportGeneratorsStore} from '../stores/ReportGeneratorsStore';
import {ChartDimensions} from '../chartConsts';
import {getDataplotInputs} from '../componenets/Chart/utils';

const parseDecimalFormat = (pattern: string) => {
    const charCode = pattern.slice(3, 5);
    return String.fromCharCode(Number(charCode));
};

const handleDataPlotValue = (text: string): string => {
    if (typeof text !== 'string') {
        return null;
    }
    const regex = new RegExp(/&#0\d{2};/g);
    if (regex.test(text)) {
        return text.replace(regex, parseDecimalFormat);
    } else {
        return !!text ? text : 'NULL';
    }
};

const getDataPlotValuesByType = (eventObj): string[] => {
    const {sender, data} = eventObj;
    const type = sender.args.type;
    if (type === SingleSeriesChartTypesOptions.angulargauge) {
        return null;
    }
    const dataplotInputs = getDataplotInputs(type);
    const dataplotValues = dataplotInputs.map(plot => {
        const {datasetName, id} = data;
        if (plot === ChartDimensions.category) {
            const dataIndex = data.dataIndex;
            const value = sender.args.dataSource.dataset[0].data[dataIndex];
            const {originalCategoryValue} = value;
            return handleDataPlotValue(originalCategoryValue);
        } else if (MultiSeriesChartTypesOptions[type]) {
            if (plot === ChartDimensions.series) {
                const seriesData = sender.args.dataSource.dataset;
                const serie = seriesData.find(item => item.seriesname === datasetName);
                const origSerieName = serie?.origSerieName;
                return handleDataPlotValue(origSerieName);
            } else {
                const dataIndex = data.dataIndex;
                const value =
                    sender.args.dataSource.categories[0].category[dataIndex].originalLabel;
                return handleDataPlotValue(value);
            }
        } else {
            return handleDataPlotValue(id);
        }
    });
    return dataplotValues;
};

const getChartHeightByResolution = () => {
    const isSmallScreen = window.screen.width < 1600;
    const isLargeScale = window.devicePixelRatio >= 1.25;
    return isSmallScreen && isLargeScale ? '100%' : '80%';
};

export const createChartConfig = (config: ChartConfig, onNoDataToDisplay?: () => void): any => {
    const {
        type = SingleSeriesChartTypesOptions.column2d,
        chartName = '',
        subTitle = '',
        xAxis,
        yAxis,
        explanatoryReport = null
    } = config;

    const dataSource = {
        chart: {
            caption: chartName,
            subCaption: subTitle,
            xAxisName: xAxis?.Title || null,
            yAxisName: yAxis.Title,
            numberSuffix: '',
            theme: 'fusion',
            baseFont: 'Rubik',
            paletteColors: customPalette.join(',')
        },
        colorRange: {},
        dials: {}
    };

    const chartData = genChartDatByType(config, dataSource);

    const chartConfig = {
        type: type,
        hasRTLText: false,
        width: '95%',
        height: getChartHeightByResolution(),
        dataFormat: 'json',
        dataSource: {...chartData},
        events: {
            dataPlotClick: (eventObj, dataObj) => {
                if (explanatoryReport) {
                    const dataplotValues = getDataPlotValuesByType(eventObj);
                    const explanatoryReportTitle = explanatoryReport.Title;
                    dataplotValues &&
                        reportGeneratorsStore?.currentScreen?.setCurrPlotValues(dataplotValues);
                    reportGeneratorsStore?.currentScreen?.setCurrExpRepTitle(
                        explanatoryReportTitle
                    );
                    config.dataPlotEventHandler();
                }
            },
            nodatatodisplay: () => {
                if (onNoDataToDisplay) {
                    onNoDataToDisplay();
                }
            }
        }
    };

    return chartConfig;
};

const genChartDatByType = (config: ChartConfig, dataSource) => {
    switch (config.type) {
        case SingleSeriesChartTypesOptions.bubble:
            return genBubbleChartData(config, dataSource);
        case SingleSeriesChartTypesOptions.angulargauge:
            return genScalesChartData(config, dataSource);
        case SingleSeriesChartTypesOptions.funnel:
            return getFunnelChartData(config, dataSource);
        case MultiSeriesChartTypesOptions.msbar2d:
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.msline:
            return genMultiSeriesChartData(config, dataSource);
        default:
            return genBasicChartData(config, dataSource);
    }
};
